import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, X, Trash2 } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { api, formatPrice, resolveImageUrl } from "@/lib/api";

export default function Compare() {
  const { compare, toggleCompare, clearCompare } = useShop();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const categoryName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  if (compare.products.length === 0) {
    return (
      <div data-testid="compare-empty" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <Scale className="mx-auto text-muted-soft" size={40} />
        <h1 className="mt-6 text-3xl font-extrabold tracking-tightest text-ink">Список сравнения пуст</h1>
        <p className="mt-3 text-sm text-muted">Добавляйте товары одной категории, чтобы сравнить характеристики бок о бок.</p>
        <Link to="/" className="mt-8 inline-block rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white">В каталог</Link>
      </div>
    );
  }

  return (
    <div data-testid="compare-page" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Сравнение</div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-tightest text-ink md:text-5xl">
            {compare.count} {compare.count === 1 ? "товар" : "товаров"}
          </h1>
          {compare.category_slug && (
            <p className="mt-2 text-sm text-muted">Категория: <span className="text-ink">{categoryName(compare.category_slug)}</span></p>
          )}
        </div>
        <button
          data-testid="compare-clear"
          onClick={clearCompare}
          className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-danger hover:text-white"
        >
          <Trash2 size={14} /> Очистить
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="compare-table w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr>
              <th className="border-b border-r border-line p-4 text-left text-xs font-bold uppercase tracking-[0.16em] text-muted-soft">
                Характеристика
              </th>
              {compare.products.map((p) => (
                <th key={p.id} className="border-b border-r border-line p-4 text-left align-top">
                  <div className="relative">
                    <button
                      data-testid={`compare-remove-${p.slug}`}
                      onClick={() => toggleCompare(p.id)}
                      className="absolute right-0 top-0 grid h-7 w-7 place-items-center rounded-full border border-line text-muted hover:bg-danger hover:text-white"
                    >
                      <X size={13} />
                    </button>
                    <Link to={`/product/${p.slug}`} className="block">
                      <div className="mb-3 overflow-hidden rounded-xl bg-paper">
                        <img src={resolveImageUrl(p.images?.[0])} alt="" className="h-32 w-full object-cover" />
                      </div>
                      <div className="text-sm font-bold tracking-tight text-ink hover:underline">{p.name}</div>
                      <div className="mt-1 text-xs text-muted">{p.subtitle}</div>
                      <div className="mt-2 font-extrabold text-ink">от {formatPrice(p.min_price)}</div>
                    </Link>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-b border-r border-line p-4 text-xs font-bold uppercase tracking-[0.14em] text-muted-soft">Бренд</td>
              {compare.products.map((p) => (
                <td key={p.id} className="border-b border-r border-line p-4 capitalize">{p.brand_slug}</td>
              ))}
            </tr>
            <tr>
              <td className="border-b border-r border-line p-4 text-xs font-bold uppercase tracking-[0.14em] text-muted-soft">Категория</td>
              {compare.products.map((p) => (
                <td key={p.id} className="border-b border-r border-line p-4">{categoryName(p.category_slug)}</td>
              ))}
            </tr>
            <tr>
              <td className="border-b border-r border-line p-4 text-xs font-bold uppercase tracking-[0.14em] text-muted-soft">Цена от</td>
              {compare.products.map((p) => (
                <td key={p.id} className="border-b border-r border-line p-4 font-bold text-ink">{formatPrice(p.min_price)}</td>
              ))}
            </tr>
            {compare.spec_keys.map((key) => (
              <tr key={key}>
                <td className="border-b border-r border-line p-4 text-xs font-bold uppercase tracking-[0.14em] text-muted-soft">{key}</td>
                {compare.products.map((p) => (
                  <td key={p.id} className="border-b border-r border-line p-4 text-ink">
                    {(p.specs && p.specs[key]) || <span className="text-muted-soft">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
